import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import clsx from "clsx";

interface NeonButtonProps {
  to?: string;
  onClick?: () => void;
  children: React.ReactNode;
  variant?: "green" | "gradient";
  size?: "sm" | "md" | "lg";
  className?: string;
  breathe?: boolean;
  ariaLabel?: string;
}

const NeonButton: React.FC<NeonButtonProps> = ({
  to,
  onClick,
  children,
  variant = "green",
  size = "md",
  className = "",
  breathe = false,
  ariaLabel,
}) => {
  const base =
    "inline-flex items-center justify-center font-semibold rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-neon-green focus:ring-offset-2 focus:ring-offset-space whitespace-nowrap";

  const variants = {
    green:
      "bg-neon-green text-space hover:shadow-neon-green-lg shadow-neon-green",
    gradient:
      "bg-gradient-brand text-space hover:opacity-90 shadow-neon-green",
  };

  const sizes = {
    sm: "px-4 py-2 text-body-sm",
    md: "px-6 py-2.5 text-body-sm",
    lg: "px-8 py-4 text-body",
  };

  const combined = clsx(
    base,
    variants[variant],
    sizes[size],
    breathe && "animate-breathe",
    className
  );

  if (to) {
    return (
      <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
        <Link to={to} className={combined} aria-label={ariaLabel}>
          {children}
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.button
      type="button"
      onClick={onClick}
      className={combined}
      aria-label={ariaLabel}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
    >
      {children}
    </motion.button>
  );
};

export default NeonButton;
